import type { CSSProperties, ReactNode } from 'react';

import useIntersectionObserver from '@/hooks/shared/useIntersectionObserver';

type RevealCustomProperties = CSSProperties & {
  '--intersection-duration': string;
  '--intersection-ease': string;
  '--intersection-hidden-filter': string;
  '--intersection-hidden-opacity': number;
  '--intersection-hidden-transform': string;
  '--intersection-visible-filter': string;
  '--intersection-visible-opacity': number;
  '--intersection-visible-transform': string;
};

export type IntersectionRevealProps = {
  children: ReactNode;
  delay?: number;
  duration?: number;
  element?: 'li';
  ease?: string;
  hiddenFilter?: string;
  hiddenOpacity?: number;
  hiddenTransform?: string;
  mask?: boolean;
  once?: boolean;
  visibleFilter?: string;
  visibleOpacity?: number;
  visibleTransform?: string;
};

const CSS_EASES: Record<string, string> = {
  none: 'linear',
  'power1.in': 'cubic-bezier(0.55, 0.085, 0.68, 0.53)',
  'power1.inOut': 'cubic-bezier(0.455, 0.03, 0.515, 0.955)',
  'power1.out': 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  'power2.in': 'cubic-bezier(0.55, 0.055, 0.675, 0.19)',
  'power2.inOut': 'cubic-bezier(0.645, 0.045, 0.355, 1)',
  'power2.out': 'cubic-bezier(0.215, 0.61, 0.355, 1)',
  'power3.in': 'cubic-bezier(0.895, 0.03, 0.685, 0.22)',
  'power3.inOut': 'cubic-bezier(0.77, 0, 0.175, 1)',
  'power3.out': 'cubic-bezier(0.165, 0.84, 0.44, 1)',
  'power4.in': 'cubic-bezier(0.755, 0.05, 0.855, 0.06)',
  'power4.inOut': 'cubic-bezier(0.86, 0, 0.07, 1)',
  'power4.out': 'cubic-bezier(0.23, 1, 0.32, 1)',
  'back.in': 'cubic-bezier(0.6, -0.28, 0.735, 0.045)',
  'back.inOut': 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  'back.out': 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  'circ.in': 'cubic-bezier(0.6, 0.04, 0.98, 0.335)',
  'circ.inOut': 'cubic-bezier(0.785, 0.135, 0.15, 0.86)',
  'circ.out': 'cubic-bezier(0.075, 0.82, 0.165, 1)',
  'expo.in': 'cubic-bezier(0.95, 0.05, 0.795, 0.035)',
  'expo.inOut': 'cubic-bezier(1, 0, 0, 1)',
  'expo.out': 'cubic-bezier(0.19, 1, 0.22, 1)',
  'sine.in': 'cubic-bezier(0.47, 0, 0.745, 0.715)',
  'sine.inOut': 'cubic-bezier(0.445, 0.05, 0.55, 0.95)',
  'sine.out': 'cubic-bezier(0.39, 0.575, 0.565, 1)',
};

function resolveCssEase(ease: string) {
  return CSS_EASES[ease] ?? CSS_EASES['power1.out'];
}

export default function IntersectionReveal({
  children,
  delay = 0,
  duration = 0.5,
  element,
  ease = 'power1.out',
  hiddenFilter = 'none',
  hiddenOpacity = 0,
  hiddenTransform = 'none',
  mask = false,
  once = true,
  visibleFilter = 'none',
  visibleOpacity = 1,
  visibleTransform = 'none',
}: IntersectionRevealProps) {
  const targetRef = useIntersectionObserver<HTMLDivElement>({
    delayMs: delay * 1000,
    once,
  });
  const style: RevealCustomProperties = {
    '--intersection-duration': `${duration * 1000}ms`,
    '--intersection-ease': resolveCssEase(ease),
    '--intersection-hidden-filter': hiddenFilter,
    '--intersection-hidden-opacity': hiddenOpacity,
    '--intersection-hidden-transform': hiddenTransform,
    '--intersection-visible-filter': visibleFilter,
    '--intersection-visible-opacity': visibleOpacity,
    '--intersection-visible-transform': visibleTransform,
  };
  const Element = (element ?? 'div') as 'div';
  const target = (
    <Element
      className="intersection-reveal target"
      ref={targetRef}
      style={style}
    >
      {children}
    </Element>
  );

  if (element) {
    return target;
  }

  return <div>{mask ? <div className="overflow-hidden">{target}</div> : target}</div>;
}
